const HEADER_FIELDS = new Set(['front', 'back', 'question', 'answer', 'term', 'definition']);
const ANKI_METADATA_LINE_RE = /^#\s*(separator|html|deck|notetype|columns|tags|guid|fields?|notes?)\b/i;

const isMetadataLine = (line) => ANKI_METADATA_LINE_RE.test(line);

const toCard = (front, back, index) => {
  const normalizedFront = typeof front === 'string' ? front.trim() : '';
  const normalizedBack = typeof back === 'string' ? back.trim() : '';

  if (!normalizedFront || !normalizedBack) {
    return null;
  }

  return {
    cardId: `import-${index + 1}`,
    front: normalizedFront,
    back: normalizedBack,
    tags: []
  };
};

const splitDelimitedLine = (line, delimiter) => {
  if (!line.includes('"')) {
    return line.split(delimiter).map((value) => value.trim());
  }

  const values = [];
  let current = '';
  let isInQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];

    if (character === '"') {
      const nextCharacter = line[index + 1];
      if (isInQuotes && nextCharacter === '"') {
        current += '"';
        index += 1;
      } else {
        isInQuotes = !isInQuotes;
      }
      continue;
    }

    if (character === delimiter && !isInQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += character;
  }

  values.push(current.trim());
  return values.map((value) => value.replace(/^"|"$/g, '').trim());
};

const parseStructuredRows = (lines, delimiter) => {
  const header = splitDelimitedLine(lines[0], delimiter).map((value) => value.toLowerCase());
  const frontIndex = header.findIndex((value) => HEADER_FIELDS.has(value) && (value === 'front' || value === 'question' || value === 'term'));
  const backIndex = header.findIndex((value) => HEADER_FIELDS.has(value) && (value === 'back' || value === 'answer' || value === 'definition'));

  if (frontIndex === -1 || backIndex === -1) {
    return null;
  }

  const cards = [];

  for (let index = 1; index < lines.length; index += 1) {
    const columns = splitDelimitedLine(lines[index], delimiter);
    const card = toCard(columns[frontIndex], columns[backIndex], cards.length);
    if (card) {
      cards.push(card);
    }
  }

  return cards;
};

export const parseFlashcardImportText = (input) => {
  if (typeof input !== 'string') {
    return [];
  }

  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmedInput);
    if (Array.isArray(parsed)) {
      const cards = parsed
        .map((item, index) => toCard(item.front || item.question || item.term, item.back || item.answer || item.definition, index))
        .filter(Boolean);

      if (cards.length) {
        return cards;
      }
    }
  } catch (error) {
    // Ignore JSON parsing failures and fall back to text parsing.
  }

  const lines = trimmedInput.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const contentLines = lines.filter((line) => !isMetadataLine(line));

  if (!contentLines.length) {
    return [];
  }

  if (contentLines.some((line) => line.includes('\t'))) {
    const structured = parseStructuredRows(contentLines, '\t');
    if (structured) {
      return structured;
    }

    return contentLines
      .map((line, index) => {
        const [front, back] = splitDelimitedLine(line, '\t');
        return toCard(front, back, index);
      })
      .filter(Boolean);
  }

  if (contentLines.some((line) => line.includes(','))) {
    const structured = parseStructuredRows(contentLines, ',');
    if (structured) {
      return structured;
    }
  }

  return contentLines
    .map((line, index) => {
      const separators = [' :: ', ' | ', ' - ', ':'];
      for (const separator of separators) {
        const parts = line.split(separator);
        if (parts.length >= 2) {
          return toCard(parts[0], parts.slice(1).join(separator), index);
        }
      }
      return null;
    })
    .filter(Boolean);
};